import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useOidc } from '@nohara/react-oidc';
import {
  Box,
  Button,
  Typography,
  TextField,
  Step,
  StepLabel,
  Stepper,
  Paper,
  Skeleton,
} from '@mui/material';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useUser } from '../api/get-user';
import { useUpdateUser } from '../api/update-user';

import { paths } from '@/config/paths';

// Form validation schema
const formSchema = z.object({
  name: z.string().min(1, { message: 'users.form.name.required' }),
});

type FormValues = z.infer<typeof formSchema>;

export const OnboardingForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId } = useOidc();
  const { data: user, isLoading } = useUser(userId);
  const updateUser = useUpdateUser();
  const [activeStep, setActiveStep] = useState(() => {
    if (!user?.name) return 0;
    return 1;
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: user?.name || '',
    },
  });

  const onSubmit = async (values: FormValues) => {
    if (!userId) return;

    try {
      await updateUser.mutateAsync({
        userId,
        data: {
          name: values.name,
        },
      });

      setActiveStep(1);
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  if (isLoading) {
    return (
      <Paper sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant='text' height={40} width={300} sx={{ mb: 1 }} />
          <Skeleton variant='text' height={24} width={400} />
        </Box>

        <Skeleton variant='rectangular' height={70} sx={{ mb: 4 }} />

        <Box sx={{ mb: 3 }}>
          <Skeleton variant='rectangular' height={56} />
        </Box>

        <Skeleton variant='rectangular' height={42} width='100%' />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant='h4' gutterBottom>
          {t('onboarding.title')}
        </Typography>
        <Typography variant='body1' color='text.secondary'>
          {t('onboarding.subtitle')}
        </Typography>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        <Step>
          <StepLabel>{t('onboarding.steps.profile', 'Profile Information')}</StepLabel>
        </Step>
        <Step>
          <StepLabel>{t('onboarding.steps.complete', 'Complete')}</StepLabel>
        </Step>
      </Stepper>

      {activeStep === 0 ? (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ mb: 3 }}>
            <TextField
              label={t('users.form.name.label', 'Name')}
              fullWidth
              {...register('name')}
              error={!!errors.name}
              helperText={errors.name ? t(errors.name.message || '') : ''}
              autoFocus
            />
          </Box>
          <Button type='submit' variant='contained' disabled={updateUser.isPending} fullWidth>
            {updateUser.isPending ? t('common.submitting') : t('common.continue')}
          </Button>
        </form>
      ) : (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant='h5' gutterBottom>
            {t('onboarding.complete.title')}
          </Typography>
          <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
            {t('onboarding.complete.subtitle')}
          </Typography>
          <Button variant='contained' onClick={() => navigate(paths.org.root.path)}>
            {t('onboarding.complete.cta')}
          </Button>
        </Box>
      )}
    </Paper>
  );
};
